/**
 * Underutilized Vehicles Report
 * Rapport för underutnyttjade fordon med varningar (Uppsala krav 5.5.1.4)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import DateRangeFilter from '@/components/common/DateRangeFilter';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import {
  TruckIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { DataTable, ReportPageLayout, LoadingState, SummaryCard } from '@/components/common';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';

interface UnderutilizedVehicle {
  vehicleId: number;
  registrationNumber: string;
  make: string;
  model: string;
  organizationName: string;
  totalTrips: number;
  totalDistance: number;
  totalDrivingTime: number;
  avgTripsPerDay: number;
  avgDistancePerDay: number;
  daysActive: number;
  daysInactive: number;
  utilizationScore: number;
  warnings: string[];
}

interface UtilizationSummary {
  totalVehicles: number;
  activeVehicles: number;
  underutilizedVehicles: number;
  avgUtilizationScore: number;
  totalDistanceFleet: number;
  totalTripsFleet: number;
}

interface TrendData {
  period: string;
  activeVehicles: number;
  inactiveVehicles: number;
  utilizationRate: number;
  totalTrips: number;
  totalDistance: number;
}

const UnderutilizedReport: React.FC = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [summary, setSummary] = useState<UtilizationSummary | null>(null);
  const [vehicles, setVehicles] = useState<UnderutilizedVehicle[]>([]);
  const [trend, setTrend] = useState<TrendData[]>([]);
  const [showOnlyWarnings, setShowOnlyWarnings] = useState(false);

  // Hämta data
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedOrg || !dateRange.startDate || !dateRange.endDate) return;

      setLoading(true);
      try {
        const [vehiclesRes, trendRes] = await Promise.all([
          api.get('/api/reports/underutilized', {
            params: {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              organizationId: selectedOrg,
            },
          }),
          api.get('/api/reports/underutilized/trend', {
            params: {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              organizationId: selectedOrg,
              groupBy: 'week',
            },
          }),
        ]);

        if (vehiclesRes.data?.success) {
          setSummary(vehiclesRes.data.data.summary);
          setVehicles(vehiclesRes.data.data.vehicles);
        }
        if (trendRes.data?.success) {
          setTrend(trendRes.data.data);
        }
      } catch {
        // Error fetching data - handled silently
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedOrg, dateRange]);

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 30) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 70) return 'bg-green-100';
    if (score >= 30) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/api/reports/underutilized/export', {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          organizationId: selectedOrg,
          format: 'csv',
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `underutilized_${dateRange.startDate}_${dateRange.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // Export error - handled silently
    }
  };

  // Filtrera fordon
  const filteredVehicles = showOnlyWarnings
    ? vehicles.filter(v => v.warnings.length > 0)
    : vehicles;

  // Pie chart data
  const pieData = summary ? [
    { name: 'Fullt utnyttjade', value: summary.totalVehicles - summary.underutilizedVehicles, color: '#22c55e' },
    { name: 'Underutnyttjade', value: summary.underutilizedVehicles, color: '#ef4444' },
  ] : [];

  return (
    <ReportPageLayout
      title={t('underutilized.title') || 'Underutnyttjade fordon'}
      description={t('underutilized.description') || 'Identifiera fordon med lågt utnyttjande och generera varningar'}
      icon={<TruckIcon className="w-6 h-6 text-primary" />}
      actions={
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:bg-bgSecondary"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
          {t('common.export') || 'Exportera'}
        </button>
      }
      filters={
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">
              {t('common.organization') || 'Organisation'}
            </label>
            <OrgTreeSelect
              value={selectedOrg}
              onChange={setSelectedOrg}
              placeholder={t('common.selectOrganization') || 'Välj organisation'}
            />
          </div>
          <DateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(field, value) =>
              setDateRange((prev) => ({ ...prev, [field]: value }))
            }
          />
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showOnlyWarnings}
                onChange={(e) => setShowOnlyWarnings(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-textSecondary">
                {t('underutilized.showOnlyWarnings') || 'Visa endast varningar'}
              </span>
            </label>
          </div>
        </div>
      }
    >
      {loading ? (
        <LoadingState />
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <SummaryCard
                title={t('underutilized.totalVehicles') || 'Totalt fordon'}
                value={summary.totalVehicles}
              />
              <SummaryCard
                title={t('underutilized.activeVehicles') || 'Aktiva fordon'}
                value={<><CheckCircleIcon className="h-6 w-6" />{summary.activeVehicles}</>}
                valueClassName="text-green-600"
              />
              <SummaryCard
                title={t('underutilized.underutilizedVehicles') || 'Med varningar'}
                value={<><ExclamationTriangleIcon className="h-6 w-6" />{summary.underutilizedVehicles}</>}
                valueClassName="text-red-600"
              />
              <SummaryCard
                title={t('underutilized.avgUtilization') || 'Snitt utnyttjande'}
                value={`${summary.avgUtilizationScore}%`}
                valueClassName={getScoreColor(summary.avgUtilizationScore)}
              />
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="bg-bgCard rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-textDefault mb-4">
                {t('underutilized.utilizationOverview') || 'Utnyttjandeöversikt'}
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Trend Chart */}
            <div className="bg-bgCard rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-textDefault mb-4">
                {t('underutilized.utilizationTrend') || 'Utnyttjandetrend'}
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="utilizationRate" stroke="#3b82f6" name="Utnyttjande (%)" />
                  <Line type="monotone" dataKey="activeVehicles" stroke="#22c55e" name="Aktiva fordon" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Vehicle Table */}
          <div className="bg-bgCard rounded-lg shadow-sm overflow-hidden">
            <DataTable<UnderutilizedVehicle>
              title={`${showOnlyWarnings ? (t('underutilized.vehiclesWithWarnings') || 'Fordon med varningar') : (t('underutilized.allVehicles') || 'Alla fordon')} (${filteredVehicles.length} st)`}
              data={filteredVehicles}
              rowKey="vehicleId"
              pageSize={20}
              emptyMessage="Inga fordonsdata"
              columns={[
                { key: 'registrationNumber', label: t('common.vehicle') || 'Fordon', sortable: true, csvValue: (_, row) => `${row.registrationNumber} ${row.make} ${row.model}`, render: (_, row) => (<><div className="font-medium text-textDefault">{row.registrationNumber}</div><div className="text-sm text-textSecondary">{row.make} {row.model}</div></>) },
                { key: 'totalTrips', label: t('common.trips') || 'Resor', align: 'right', sortable: true },
                { key: 'totalDistance', label: t('common.distance') || 'Distans', align: 'right', sortable: true, render: (v) => (v as number).toLocaleString() + ' km' },
                { key: '_daysActive', label: t('underutilized.activeDays') || 'Aktiva dagar', align: 'center', sortable: true, sortKey: 'daysActive', csvValue: (_, row) => `${row.daysActive} / ${row.daysInactive}`, render: (_, row) => (<><span className="text-green-600">{row.daysActive}</span><span className="text-textSecondary"> / </span><span className="text-red-600">{row.daysInactive}</span></>) },
                { key: 'utilizationScore', label: t('underutilized.utilization') || 'Utnyttjande', align: 'center', sortable: true, render: (v) => { const n = Number(v ?? 0); return <span className={`px-3 py-1 rounded-full font-bold ${getScoreBg(n)} ${getScoreColor(n)}`}>{n}%</span>; } },
                { key: 'warnings', label: t('underutilized.warnings') || 'Varningar', sortable: true, csvValue: (v) => (v as string[]).length > 0 ? (v as string[]).join('; ') : 'OK', render: (v) => (v as string[]).length > 0 ? (
                  <div className="space-y-1">{(v as string[]).map((w, i) => <div key={i} className="flex items-center gap-1 text-sm text-red-600"><ExclamationTriangleIcon className="h-4 w-4" />{w}</div>)}</div>
                ) : <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircleIcon className="h-4 w-4" />OK</span> },
              ]}
              features={{ csvExport: true, search: true, stickyHeader: true }}
            />
          </div>
        </>
      )}
    </ReportPageLayout>
  );
};

export default UnderutilizedReport;
